/**
 * Date helper utilities — single source for age calculation used across the engine.
 * Ported verbatim from frontend/src/utils/dateHelpers.js.
 */

/**
 * A `YYYY-MM-DD` strategy input as a LOCAL date, or null when absent or malformed.
 *
 * Built from the components rather than `new Date(iso)`, which parses a bare date string as UTC and so
 * lands on the previous day for anyone east of Greenwich — every Australian user. These dates are
 * compared against each other and printed into advice documents, so a silent one-day shift would put
 * an asset sale on the wrong side of an entry date.
 */
export function parseIsoDate(raw: string | null | undefined): Date | null {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(raw ?? ''));
    return match ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])) : null;
}

/** "1 December 2026" — how dates read in client-facing documents. */
export function formatLongDate(date: Date): string {
    return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' });
}

/** Calculate age from date of birth. */
export function calculateAge(dob: string | Date | null | undefined, refDate: Date = new Date()): number | null {
    if (!dob) return null;
    const birthDate = new Date(dob);
    const referenceDate = new Date(refDate);
    let age = referenceDate.getFullYear() - birthDate.getFullYear();
    const monthDiff = referenceDate.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && referenceDate.getDate() < birthDate.getDate())) {
        age--;
    }
    return age;
}
